/**
 * RankPill — tiny chip beside a player's name showing their movement in
 * the leaderboard since the previous period.
 *
 *   ↑3   — moved up 3 places (green)
 *   ↓2   — moved down 2 places (red)
 *   –    — no change (muted)
 *
 * `delta === null` (no baseline yet — first-period visit) renders nothing
 * so we don't lie about movement that didn't happen.
 */
import React from "react";
import SoftBox from "../SoftBox";

const FONT = `"DM Sans", -apple-system, BlinkMacSystemFont, sans-serif`;

const STYLES = {
  up: { bg: "#EFFAF0", fg: "#119031" },
  down: { bg: "#FEF2F2", fg: "#B42318" },
  same: { bg: "#F2F4F7", fg: "#98A2B3" },
};

const PillBase = ({ children, tone }) => (
  <SoftBox
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: "2px",
      padding: "2px 7px",
      borderRadius: "999px",
      background: STYLES[tone].bg,
      color: STYLES[tone].fg,
      fontFamily: FONT,
      fontSize: "11px",
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: "0.01em",
      flexShrink: 0,
    }}
  >
    {children}
  </SoftBox>
);

export default function RankPill({ delta }) {
  // Null = no baseline yet (first time we've seen this period). Skip render.
  if (delta === null || delta === undefined) return null;

  if (delta === 0) {
    return <PillBase tone="same">–</PillBase>;
  }
  if (delta > 0) {
    return <PillBase tone="up">↑{delta}</PillBase>;
  }
  return <PillBase tone="down">↓{Math.abs(delta)}</PillBase>;
}
